import React, { useState, useEffect, useCallback, memo } from 'react';
import { Check, PlayCircle, AlertCircle, PartyPopper, Target, Clock, SkipForward, Loader2, ImagePlus, X, ChevronDown } from 'lucide-react';
import { getImageUrl } from '../config/runtime';
import { interactiveCardsAPI } from '../services/api';

// SVG Pattern Component - matches InteractiveCards
const GridPattern = memo(({ className = "" }) => (
  <div className={`absolute inset-0 w-full h-full ${className}`}>
    <img
      src="/svg/pattern1.svg"
      alt=""
      className="w-full h-full object-cover opacity-[0.04] dark:opacity-[0.1]"
      style={{ filter: 'brightness(0) saturate(100%)' }}
    />
  </div>
));

/**
 * GuideCard - One-way guidance for flow-state tasks
 * 
 * DESIGN PHILOSOPHY (Text-First, Trust-Based):
 * ─────────────────────────────────────────────
 * 1. The card is COMPLETE with just the instruction text
 * 2. Image visualization is an optional enhancement, not a requirement
 * 3. Header layout harmonized with InteractiveCards for visual consistency
 * 4. "Done" = trust the user completed the step (no nagging for proof)
 * 
 * GUIDE INTENTS:
 * - do_step: Sequential action → "Done"
 * - attention: Direct focus → "Got it"  
 * - checkpoint: Pre-action readiness → "Ready"
 * - milestone: Celebration/transition → "Continue"
 */
const GuideCard = memo(({ 
  card, 
  onDismiss, 
  onSkip,
  onOptionSelect,
  disabled = false,
  isDismissed = false,
  selectedOptionId = null,
  messageId = null,
  demoMode = false,
  /** When true (project.settings.auto_instruction_images), show "Creating visual…" until server pushes the image. */
  autoInstructionImages = false,
}) => {
  const {
    title,
    subtitle,
    guide_intent = 'do_step',
    instruction_prompt,
    options = [],
  } = card;

  // UI expansion state - default to expanded (text-first, show instructions immediately)
  const [isExpanded, setIsExpanded] = useState(true);
  
  // State for USER-CONTROLLED timer
  const [timerDrawerOpen, setTimerDrawerOpen] = useState(false);
  const [timerDuration, setTimerDuration] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [timerComplete, setTimerComplete] = useState(false);
  const [customTimerInput, setCustomTimerInput] = useState('');
  
  // State for instruction image
  const [instructionImageUrl, setInstructionImageUrl] = useState(card.instruction_image_url || null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [autoImagePending, setAutoImagePending] = useState(false);

  // Server can push an auto-generated image after mount (per-starter
  // auto_instruction_images) — sync it in without clobbering a local one.
  useEffect(() => {
    if (card.instruction_image_url && card.instruction_image_url !== instructionImageUrl) {
      setInstructionImageUrl(card.instruction_image_url);
      setAutoImagePending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.instruction_image_url]);

  // Proactive generation runs server-side after the card appears — mirror the
  // manual "Visualize" loading state so users know something is coming.
  useEffect(() => {
    if (
      autoInstructionImages &&
      instruction_prompt &&
      !instructionImageUrl &&
      !imageError &&
      !imageLoading
    ) {
      setAutoImagePending(true);
      const timeout = setTimeout(() => setAutoImagePending(false), 35000);
      return () => clearTimeout(timeout);
    }
    setAutoImagePending(false);
  }, [autoInstructionImages, instruction_prompt, instructionImageUrl, imageError, imageLoading]);

  // Default dismiss labels by intent
  const defaultDismissLabels = {
    do_step: 'Done',
    attention: 'Got it',
    checkpoint: 'Ready',
    milestone: 'Continue',
  };

  const actualDismissLabel = defaultDismissLabels[guide_intent] || 'Done';

  // Intent-based styling
  // ICON: All guide cards use INDIGO to be visually distinct from other card types
  // (observe_photo=green, learn=blue, location=yellow, spotlight=gray, forms=purple)
  // BUTTON: Unified emerald for all primary actions (clean, modern, harmonized)
  const intentConfig = {
    do_step: {
      icon: PlayCircle,
      timerBg: 'bg-emerald-500',
    },
    attention: {
      icon: AlertCircle,
      timerBg: 'bg-amber-500',
    },
    checkpoint: {
      icon: Target,
      timerBg: 'bg-indigo-500',
    },
    milestone: {
      icon: PartyPopper,
      timerBg: 'bg-fuchsia-500',
    },
  };

  // Shared icon styling - INDIGO for all guide cards (unique to this card type)
  const iconStyle = {
    bg: 'bg-indigo-100 dark:bg-indigo-400/20',
    color: 'text-indigo-600 dark:text-indigo-400',
  };

  const config = intentConfig[guide_intent] || intentConfig.do_step;
  const IconComponent = config.icon;

  // USER-CONTROLLED countdown timer logic
  useEffect(() => {
    if (!timerActive || isDismissed) return;
    
    const interval = setInterval(() => {
      setRemainingSeconds(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setTimerComplete(true);
          setTimerActive(false);
          
          // Play completion audio chime
          try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            
            oscillator.frequency.setValueAtTime(587.33, audioContext.currentTime);
            oscillator.frequency.setValueAtTime(880, audioContext.currentTime + 0.15);
            
            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
            
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.4);
          } catch (e) {
            console.log('Audio not available:', e);
          }
          
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [timerActive, isDismissed]);

  const startTimer = useCallback((seconds) => {
    setTimerDuration(seconds);
    setRemainingSeconds(seconds);
    setTimerActive(true);
    setTimerComplete(false);
    setTimerDrawerOpen(false);
  }, []);

  const stopTimer = useCallback(() => {
    setTimerActive(false);
    setRemainingSeconds(0);
    setTimerDuration(0);
    setTimerComplete(false);
  }, []);

  const handleCustomTimerStart = useCallback(() => {
    const seconds = parseInt(customTimerInput, 10);
    if (seconds > 0 && seconds <= 3600) {
      startTimer(seconds);
      setCustomTimerInput('');
    }
  }, [customTimerInput, startTimer]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleGenerateImage = useCallback(async () => {
    if (!messageId || !card.id || imageLoading) return;
    
    setImageLoading(true);
    setImageError(false);
    
    try {
      const response = await interactiveCardsAPI.explain({ messageId, cardId: card.id });
      const imageUrl = response?.data?.data?.instruction_image_url || response?.data?.instruction_image_url;
      if (imageUrl) {
        setInstructionImageUrl(imageUrl);
      } else {
        console.error('No instruction_image_url in response:', response?.data);
        setImageError(true);
      }
    } catch (err) {
      console.error('Failed to generate guide card instruction image:', err);
      setImageError(true);
    } finally {
      setImageLoading(false);
    }
  }, [messageId, card.id, imageLoading]);

  const handleDismiss = useCallback(() => {
    if (!disabled && onDismiss) {
      setTimerActive(false);
      onDismiss(card);
    }
  }, [disabled, onDismiss, card]);

  const handleSkip = useCallback(() => {
    if (!disabled && onSkip) {
      setTimerActive(false);
      onSkip(card);
    }
  }, [disabled, onSkip, card]);

  const handleOptionSelect = useCallback((option) => {
    if (!disabled && onOptionSelect) {
      setTimerActive(false);
      onOptionSelect(option, card);
    }
  }, [disabled, onOptionSelect, card]);

  const selectedOption = selectedOptionId ? options.find(opt => opt.id === selectedOptionId) : null;
  const isImageGenerating = imageLoading || autoImagePending;
  // Show Visualize button if instruction_prompt exists and no image yet (manual path only)
  const canGenerateImage = instruction_prompt && !instructionImageUrl && !isImageGenerating && !autoInstructionImages;
  const showImageArea = instructionImageUrl || isImageGenerating || imageError;
  // Determine if the card has expandable content
  const hasExpandableContent = instruction_prompt || canGenerateImage || instructionImageUrl || showImageArea;
  // Check for "Next step" badge (scaffolding chain indicator)
  const showNextStepBadge = card.ui_hints?.show_next_step_badge === true;

  // Render subtitle with inline "Next:" prefix (harmonized with InteractiveCards)
  const renderSubtitle = useCallback(() => {
    const trimmed = (subtitle || '').trim();
    if (!trimmed) return null;

    if (showNextStepBadge) {
      return (
        <span>
          <span className="inline-flex font-medium text-emerald-500 dark:text-emerald-400 mr-1">
            Next:
          </span>
          <span>{trimmed}</span>
        </span>
      );
    }

    return trimmed;
  }, [subtitle, showNextStepBadge]);

  // Format instruction_prompt into clean steps (shared between active and dismissed)
  const formatInstructionSteps = (prompt) => {
    if (!prompt) return null;
    
    // Split by common step separators
    let steps = prompt
      .split(/(?:Panel-?\d+:|Step \d+:|\d+\.\s+)/i)
      .map(s => s.trim())
      .filter(s => s.length > 0);
    
    // If no clear separators, try periods/semicolons
    if (steps.length <= 1) {
      steps = prompt
        .split(/[.;]\s+/)
        .map(s => s.trim())
        .filter(s => s.length > 0);
    }
    
    // Clean up step text
    const cleanedSteps = steps.map(step => {
      return step
        .replace(/^[\d\s.\-*+)\]}\s]*/, '')
        .replace(/[\d\s.\-*+)\]}\s]*$/, '')
        .trim();
    }).filter(s => s.length > 0);
    
    return cleanedSteps;
  };

  const instructionSteps = formatInstructionSteps(instruction_prompt);

  // ════════════════════════════════════════════════════════════════════════════
  // DISMISSED STATE - Stays expanded to show what was done, but no action button
  // ════════════════════════════════════════════════════════════════════════════
  if (isDismissed) {
    return (
      <div className="text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-emerald-300 dark:border-emerald-700 rounded-2xl shadow-sm">
        {/* HEADER ROW */}
        <div className="flex min-h-[64px] border-b border-gray-200 dark:border-gray-600/30">
          {/* Full-height icon on the left - INDIGO for guide cards */}
          <div className="relative w-16 min-h-full overflow-hidden flex-shrink-0">
            <div className={`absolute inset-0 w-full h-full ${iconStyle.bg} ${iconStyle.color} rounded-tl-2xl flex items-center justify-center overflow-hidden`}>
              <GridPattern />
              <div className="relative z-10">
                <div><IconComponent size={24} strokeWidth={2.5} /></div>
              </div>
            </div>
          </div>
          
          {/* Content on the right with padding */}
          <div className="flex-1 py-4 px-4 sm:px-5 flex items-center min-w-0">
            <div className="flex items-center justify-between w-full gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-[15px] sm:text-base font-semibold leading-tight text-gray-900 dark:text-gray-100">
                  {title}
                </h3>
                {subtitle && (
                  <p className="mt-0.5 text-[13px] text-gray-600 dark:text-gray-400 leading-snug line-clamp-3">
                    {renderSubtitle()}
                  </p>
                )}
                {selectedOption && (
                  <p className="mt-0.5 text-[13px] text-emerald-600 dark:text-emerald-400 leading-snug font-medium">
                    → {selectedOption.label}
                  </p>
                )}
              </div>
              
              {/* Right side: completion checkmark */}
              <div className="flex-shrink-0 flex items-center gap-2">
                <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                  <Check size={12} className="text-white" strokeWidth={3} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* EXPANDED CONTENT - Always visible in dismissed state */}
        <div className="p-4 sm:p-5 space-y-4">
          {/* Generated image - full width, above content (matches active state) */}
          {instructionImageUrl && (
            <div className="-mx-4 sm:-mx-5 -mt-4 sm:-mt-5 mb-4 overflow-hidden opacity-75">
              <img
                src={demoMode ? instructionImageUrl : getImageUrl(instructionImageUrl)}
                alt={title}
                className="w-full h-auto object-cover bg-gray-50 dark:bg-gray-800"
              />
            </div>
          )}

          {/* Instruction steps */}
          {instructionSteps && instructionSteps.length > 0 && (
            <ol className="space-y-2">
              {instructionSteps.map((step, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[11px] font-semibold text-gray-400 dark:text-gray-500 mt-0.5">
                    {index + 1}
                  </span>
                  <span className="text-[14px] text-gray-500 dark:text-gray-400 leading-relaxed flex-1">
                    {step}
                  </span>
                </li>
              ))}
            </ol>
          )}
          
          {/* Show subtitle as fallback if no instruction steps */}
          {!instructionSteps && subtitle && (
            <p className="text-[14px] text-gray-500 dark:text-gray-400 leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>
      </div>
    );
  }

  const showSkip = guide_intent === 'do_step';

  // ════════════════════════════════════════════════════════════════════════════
  // ACTIVE STATE - Expanded by default, with clear instruction hierarchy
  // ════════════════════════════════════════════════════════════════════════════

  return (
    <div 
      className={`
        dark:bg-gray-800 bg-white text-gray-700 dark:text-gray-300 
        border border-gray-200 dark:border-gray-700 rounded-2xl 
        transition-shadow duration-200 select-none
        ${isExpanded ? 'shadow-md' : 'shadow-sm hover:shadow-md'}
      `}
    >
      {/* ─────────────────────────────────────────────────────────────────────
          HEADER ROW - Clickable to expand/collapse, matches InteractiveCards
          ───────────────────────────────────────────────────────────────────── */}
      <div 
        className={`flex min-h-[64px] cursor-pointer ${isExpanded ? 'border-b border-gray-200 dark:border-gray-600/30' : ''}`}
        onClick={() => hasExpandableContent && setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && hasExpandableContent) {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
        aria-expanded={isExpanded}
      >
        {/* Full-height icon on the left - INDIGO for guide cards (unique to this type) */}
        <div className="relative w-16 min-h-full overflow-hidden flex-shrink-0">
          <div className={`absolute inset-0 w-full h-full ${iconStyle.bg} ${iconStyle.color} ${isExpanded ? 'rounded-tl-2xl' : 'rounded-l-2xl'} flex items-center justify-center overflow-hidden`}>
            <GridPattern />
            <div className="relative z-10">
              <div><IconComponent size={24} strokeWidth={2.5} /></div>
            </div>
          </div>
        </div>
        
        {/* Content on the right with padding */}
        <div className="flex-1 py-4 px-4 sm:px-5 flex items-center min-w-0">
          <div className="flex items-center justify-between w-full gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-[15px] sm:text-base font-semibold leading-tight text-gray-900 dark:text-gray-100">
                {title}
              </h3>
              {subtitle && (
                <p className="mt-0.5 text-[13px] text-gray-600 dark:text-gray-400 leading-snug line-clamp-3">
                  {renderSubtitle()}
                </p>
              )}
            </div>
            
            {/* Right side: chevron */}
            <div className="flex-shrink-0 flex items-center gap-2">
              {hasExpandableContent && (
                <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                  <ChevronDown size={18} className="text-gray-400 dark:text-gray-500" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────────────
          EXPANDED CONTENT - Instructions + optional tools
          ───────────────────────────────────────────────────────────────────── */}
      {isExpanded && (
        <div className="p-4 sm:p-5 space-y-4">
          
          
          {/* GENERATED IMAGE - Above content, full-width (matches InteractiveCards) */}
          {showImageArea && (
            <div className="-mx-4 sm:-mx-5 -mt-4 sm:-mt-5 pb-1">
              {/* Loading state: covers API generation, auto-generation, and image download */}
              {(isImageGenerating || (instructionImageUrl && !imageLoaded && !imageError)) && (
                <div className="relative overflow-hidden bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-900/20 dark:to-violet-900/20">
                  {/* Skeleton shimmer effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 dark:via-white/5 to-transparent animate-shimmer" 
                       style={{ backgroundSize: '200% 100%' }} />
                  
                  {/* Content */}
                  <div className="relative flex flex-col items-center justify-center py-10 gap-3">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-full bg-indigo-100 dark:bg-indigo-800/40 flex items-center justify-center">
                        <ImagePlus size={22} className="text-indigo-400 dark:text-indigo-400" />
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center shadow-sm">
                        <Loader2 size={12} className="animate-spin text-indigo-500" />
                      </div>
                    </div>
                    <span className="text-sm font-medium text-indigo-600/80 dark:text-indigo-400/80">
                      {isImageGenerating ? 'Creating visual...' : 'Loading image...'}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Loaded image */}
              {instructionImageUrl && !imageError && (
                <div className={`overflow-hidden transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0 absolute'}`}>
                  <img
                    src={demoMode ? instructionImageUrl : getImageUrl(instructionImageUrl)}
                    alt={title}
                    className="w-full h-auto object-cover bg-gray-50 dark:bg-gray-800"
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageError(true)}
                  />
                </div>
              )}
              
              {/* Error state */}
              {imageError && (
                <div className="flex items-center justify-center gap-2 py-4 text-gray-400 text-sm">
                  <AlertCircle size={16} />
                  <span>Failed to generate image</span>
                </div>
              )}
            </div>
          )}

          {/* ACTIVE TIMER DISPLAY - Full width when running */}
          {(timerActive || timerComplete) && (
            <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-3 border border-indigo-100 dark:border-indigo-800/30">
              {timerComplete ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Check size={18} className="text-emerald-500" />
                    <span className="font-medium text-emerald-600 dark:text-emerald-400">Time complete</span>
                  </div>
                  <button 
                    type="button"
                    onClick={stopTimer}
                    className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 px-2 py-1"
                  >
                    Reset
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="font-mono text-lg font-bold text-indigo-600 dark:text-indigo-400">
                    {formatTime(remainingSeconds)}
                  </span>
                  <div className="flex-1 h-2 bg-indigo-100 dark:bg-indigo-800/50 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-indigo-500 transition-all duration-1000 ease-linear rounded-full"
                      style={{ width: `${((timerDuration - remainingSeconds) / timerDuration) * 100}%` }}
                    />
                  </div>
                  <button 
                    type="button"
                    onClick={stopTimer}
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-indigo-100 dark:hover:bg-indigo-800/30"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
            </div>
          )}


          {/* TIMER DRAWER - Expanded timer selection (below image for better UX) */}
          {timerDrawerOpen && !timerActive && !timerComplete && (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-full sm:w-auto">Quick:</span>
                {[15, 30, 60, 120, 180, 300].map((sec) => (
                  <button
                    key={sec}
                    type="button"
                    onClick={() => startTimer(sec)}
                    className="px-3 py-1.5 rounded-lg text-sm font-semibold transition-all
                      bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500
                      hover:shadow-md active:scale-95 text-gray-700 dark:text-gray-300"
                  >
                    {sec < 60 ? `${sec}s` : `${sec / 60}m`}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Custom:</span>
                <input
                  type="number"
                  min="1"
                  max="3600"
                  value={customTimerInput}
                  onChange={(e) => setCustomTimerInput(e.target.value)}
                  placeholder="sec"
                  className="w-20 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                  onKeyDown={(e) => e.key === 'Enter' && handleCustomTimerStart()}
                />
                <button
                  type="button"
                  onClick={handleCustomTimerStart}
                  disabled={!customTimerInput || parseInt(customTimerInput, 10) <= 0}
                  className="px-4 py-1.5 rounded-lg text-sm font-semibold text-white
                    bg-indigo-500 hover:bg-indigo-600 
                    disabled:opacity-50 disabled:cursor-not-allowed
                    hover:shadow-md active:scale-95 transition-all"
                >
                  Start
                </button>
              </div>
            </div>
          )}

          {/* INSTRUCTION STEPS with floating tools */}
          <div className="relative">
            {/* Floating tools - top right of instructions section */}
            {/* Timer always available (unless actively timing), Visualize only when no image yet */}
            {!timerActive && !timerComplete && !timerDrawerOpen && (
              <div className="float-right flex flex-col items-end gap-1.5">
                {/* Visualize button - manual path only; auto starters show a spinner instead */}
                {canGenerateImage && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGenerateImage();
                    }}
                    disabled={disabled}
                    title="Visualize"
                    aria-label="Generate visual illustration"
                    className="inline-flex items-center justify-center
                      w-10 h-10 rounded-xl
                      text-indigo-600 dark:text-indigo-400 
                      bg-indigo-50 dark:bg-indigo-900/30 
                      hover:bg-indigo-100 dark:hover:bg-indigo-900/50
                      border border-indigo-200 dark:border-indigo-800/50
                      active:scale-[0.95] transition-all
                      disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ImagePlus size={18} strokeWidth={2} />
                  </button>
                )}
                {autoImagePending && (
                  <div
                    title="Creating visual..."
                    aria-label="Creating visual illustration"
                    className="inline-flex items-center justify-center
                      w-10 h-10 rounded-xl
                      text-indigo-600 dark:text-indigo-400 
                      bg-indigo-50 dark:bg-indigo-900/30 
                      border border-indigo-200 dark:border-indigo-800/50"
                  >
                    <Loader2 size={18} className="animate-spin" strokeWidth={2} />
                  </div>
                )}
                
                {/* Timer button - always available */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setTimerDrawerOpen(true);
                  }}
                  title="Timer"
                  aria-label="Set a timer"
                  className="inline-flex items-center justify-center
                    w-10 h-10 rounded-xl
                    text-gray-500 dark:text-gray-400 
                    hover:text-gray-700 dark:hover:text-gray-300
                    bg-gray-100 dark:bg-gray-700/50 
                    hover:bg-gray-200 dark:hover:bg-gray-600/50
                    active:scale-[0.95] transition-all"
                >
                  <Clock size={18} strokeWidth={2} />
                </button>
              </div>
            )}

            {/* Instruction steps - the core content */}
            {instructionSteps && instructionSteps.length > 0 ? (
              <ol className="space-y-2.5">
                {instructionSteps.map((step, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[11px] font-semibold text-gray-500 dark:text-gray-400 mt-0.5">
                      {index + 1}
                    </span>
                    <span className="text-[14px] text-gray-600 dark:text-gray-300 leading-relaxed flex-1">
                      {step}
                    </span>
                  </li>
                ))}
              </ol>
            ) : subtitle ? (
              <p className="text-[14px] text-gray-600 dark:text-gray-300 leading-relaxed">
                {subtitle}
              </p>
            ) : null}
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────────
          ACTION SECTION - Always visible, drives the interaction
          ───────────────────────────────────────────────────────────────────── */}
      <div className={`px-4 pb-4 ${isExpanded ? 'pt-1' : 'pt-4'}`}>
        {options.length > 0 ? (
          /* Multiple options - show as button group */
          <div className="flex flex-wrap gap-2">
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOptionSelect(option);
                }}
                disabled={disabled}
                className="flex-1 min-w-[120px] px-4 py-3 rounded-xl font-semibold text-sm transition-all
                  bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600
                  hover:bg-gray-200 dark:hover:bg-gray-600 hover:shadow-md active:scale-[0.98] 
                  text-gray-800 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {option.label}
              </button>
            ))}
          </div>
        ) : (
          /* Single dismiss action */
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            {/* Primary Dismiss Button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDismiss();
              }}
              disabled={disabled}
              className="flex-1 py-3.5 px-6 rounded-xl font-bold text-white text-[15px] sm:text-base
                bg-emerald-500 hover:bg-emerald-600
                shadow-sm hover:shadow-md
                transition-all duration-200 active:scale-[0.98]
                disabled:opacity-50 disabled:cursor-not-allowed
                flex items-center justify-center gap-2"
            >
              <Check size={20} strokeWidth={2.5} />
              {actualDismissLabel}
            </button>

            {/* Skip - Subtle, positioned away from primary action */}
            {/* {showSkip && onSkip && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSkip();
                }}
                disabled={disabled}
                className="sm:flex-shrink-0 self-end sm:self-center px-3 py-2 text-[13px] 
                  text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 
                  transition-colors flex items-center gap-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50"
              >
                <SkipForward size={13} />
                <span className="sm:hidden">Skip this step</span>
                <span className="hidden sm:inline">Skip</span>
              </button>
            )} */}
          </div>
        )}
      </div>
    </div>
  );
});

GuideCard.displayName = 'GuideCard';

export default GuideCard;
